import { assertEvent } from 'xstate5';

import { getModifiersCount, getPotentialSplitPortionModifiers } from '../../helpers';
import { type Guard, type SplitPortionAction } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

type Params = {
  action: SplitPortionAction;
};

/**
 * Determines if the split portion operation would exceed the maximum modifications
 * by simulating the final modifiers and checking against max modifications.
 */
export const checkIfSplitWouldExceedMaxModifications: Guard<Params> = function (
  args,
  params,
): boolean {
  const { action } = params;
  const { context, event } = args;

  assertEvent(event, [
    'customization.toggle-split-portion',
    'customization.remove-split-portion-completely',
  ]);

  const { ingredientId } = event;
  const { product, allModificationsMap, modifications, modificationLimits } = context;
  const modification = allModificationsMap?.get(ingredientId);

  // ─── Missing Data ────────────────────────────────────────────────────

  const hasMissingData =
    !modifications || !modification || !allModificationsMap || !modificationLimits || !product;

  if (hasMissingData) {
    return true;
  }

  // ─────────────────────────────────────────────────────────────────────

  const { modificationGroupName } = modification;
  const { initial, active } = modifications;

  const maxModifications = product.maxModifications;
  const hasUnlimitedModifications = maxModifications === -1;

  if (hasUnlimitedModifications) {
    return false;
  }

  const potentialModifiers = getPotentialSplitPortionModifiers({
    initialModifications: initial,
    activeModifications: active,
    ingredientId,
    modificationGroupName,
    modificationLimits,
    allModificationsMap,
    action,
  });

  const modifiersCount = getModifiersCount(potentialModifiers, allModificationsMap); // prettier-ignore

  return modifiersCount > maxModifications;
};
